import { useEffect, useState } from 'react';

import { ErrorBoundary } from '@sorare/error-boundary';

import { Suspense } from '#src/atoms/Suspense/index.tsx';
import { useIsMobileApp } from '#src/hooks/useIsMobileApp.ts';
import { lazy } from '#src/lib/retry.ts';

const LazySidetab = lazy(async () => import('./LazySidetab.ts'), 'Sidetab');

// https://github.com/Typeform/embed/blob/main/packages/embed/src/utils/load-options-from-attributes.ts
const COMMA_PLACEHOLDER = '%ESCAPED_COMMA%';
const transformRecord = (
  value: string | null
): Record<string, string> | undefined => {
  if (!value) {
    return undefined;
  }
  const arrayOfRecordStrings = value
    .replace(/\\,/g, COMMA_PLACEHOLDER)
    .split(',')
    .filter(v => !!v)
    .map(v => v.replace(COMMA_PLACEHOLDER, ','));
  return arrayOfRecordStrings.reduce((record, recordString) => {
    const match = recordString.match(/^([^=]+)=(.*)$/);
    if (match) {
      const [, key, v] = match;
      return { ...record, [key.trim()]: v };
    }
    return record;
  }, {});
};

// The Typeform Sidetab SDK injects `buttonText` and `customIcon` into the DOM
// via innerHTML, and the postMessage payload can originate from outside our own
// page, so neutralize both as XSS sinks before forwarding them to the SDK.
export const stripMarkup = (value: unknown): string | undefined =>
  typeof value === 'string' ? value.replace(/<[^>]*>?/g, '') : undefined;

export const sanitizeIconUrl = (value: unknown): string | undefined => {
  if (typeof value !== 'string') return undefined;
  try {
    return new URL(value).protocol === 'https:' ? value : undefined;
  } catch {
    return undefined;
  }
};

export /**
 * This component will listen all posted message and will render a Typeform Sidetab component when a message
 * with name 'typeform-sidetab' is received.
 *
 * In Braze, you can create a campaign with Custom HTML Code like the following:
 *
 * ```html
 * <script>
 * window.parent.postMessage({
 *   name: 'typeform-sidetab',
 *   id: "RmLwyuie",
 *   customIcon: "https://images.typeform.com/images/mYq8cAVYDt3M",
 *   opacity: 100,
 *   autoClose: true,
 *   transitiveSearchParams: true,
 *   buttonColor: "#FFFFFF",
 *   buttonText: "Answer our survey",
 *   medium: "snippet",
 *   hidden: "email=",
 *   style: "all:unset;"
 * });
 * </script>
 * ```
 */
const SidetabHandler = () => {
  const [props, setProps] = useState<any>();
  const { isMobileApp } = useIsMobileApp();

  useEffect(() => {
    const handler = (message: MessageEvent) => {
      // Reject popups posting via window.opener; legitimate messages come from
      // the in-page Braze iframe, never from a cross-origin window we opened.
      if (message.source === window.opener) return;
      if (message?.data?.name === 'typeform-sidetab') {
        document.body.classList.remove('ab-pause-scrolling');
        const { name, hidden, buttonText, customIcon, ...rest } = message.data;
        setProps({
          ...rest,
          hidden: transformRecord(hidden),
          buttonText: stripMarkup(buttonText),
          customIcon: sanitizeIconUrl(customIcon),
        });
      }
    };
    window.addEventListener('message', handler);
    return () => window.removeEventListener('message', handler);
  }, []);

  if (!props || isMobileApp) return null;

  // ensure no configuration error can break the page
  return (
    <ErrorBoundary fallback={null}>
      <Suspense>
        <LazySidetab
          {...props}
          onClose={() => {
            setTimeout(() => {
              setProps(undefined);
            }, 2_000);
          }}
        />
      </Suspense>
    </ErrorBoundary>
  );
};
